import { Fragment, useEffect, useState } from 'react'
import {t} from 'i18next';
import { Navigate as Redirect, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil'
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { Container, Card, CardBody, CardFooter, Button, Row, Col, Input, Table, Badge } from 'reactstrap'

import env from '../../env/src_config';
import { headersState, tokenState } from '../../recoil/recoil';
import { displayDate, isNull } from '../../izUtils';
import { axiosError, errorStatus } from '../../helpers/response';
import Breadcrumbs from '../../layout/breadcrumb/Breadcrumb';
import Loader from '../../components/spinner/Loader';
import ImportRecords from '../../components/contracts/import/ImportRecords';
import ImportReport from '../../components/contracts/import/ImportReport';

const apiUrl = env.api + '/api/contract_import';

const ContractImport = () => {
    const Navigate = useNavigate();
    const headers = useRecoilValue(headersState);
    const tokenData = useRecoilValue(tokenState);
    const isAdmin = tokenData.parsedToken.type === 'admin';

    const [imports, setImports] = useState([])
    const [current, setCurrent] = useState(null)
    const [columnErrors, setColumnErrors] = useState(null)
    const [file, setFile] = useState(null)
    const [busyMessage, setBusyMessage] = useState(null)
    const [counts, setCounts] = useState(null)
    const [rowsVersion, setRowsVersion] = useState(0)
    const [selectionChanged, setSelectionChanged] = useState(false)

    const isOpen = !isNull(current) && current.status === 'analyzed';
    const canReuseFile = imports.some(item => item.status !== 'columns_mismatch');
    const selectedCount = isNull(counts) ? 0 : counts.selected_changes;

    useEffect(() => {
        if (isAdmin) getImports(true);
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const getImports = (openLatest) => {
        axios.get(apiUrl, {headers}).then(response => {
            const list = response.data.imports.data;
            setImports(list);

            const latest = list.find(item => item.status === 'analyzed');
            if (openLatest && !isNull(latest)) openImport(latest.id);
        }).catch(error => axiosError(error, Navigate));
    }

    const openImport = (id) => {
        setColumnErrors(null);
        axios.get(apiUrl + '/' + id, {headers}).then(response => {
            setCurrent(response.data.import);
            setSelectionChanged(false);
            setRowsVersion(version => version + 1);
        }).catch(error => axiosError(error, Navigate));
    }

    const analyze = (withFile) => {
        const formData = new FormData();
        if (withFile) formData.append('file', file);

        setBusyMessage(t('contracts.import.busy.analyze'));
        setCurrent(null);
        setColumnErrors(null);

        axios.post(apiUrl, formData, {headers}).then(response => {
            setBusyMessage(null);
            setFile(null);

            if (response.data.state === 'success') {
                getImports(false);
                openImport(response.data.import.id);
            } else if (!isNull(response.data.import) && !isNull(response.data.import.column_errors)) {
                getImports(false);
                setColumnErrors(response.data.import.column_errors);
            } else {
                errorStatus(response.data, t);
            }
        }).catch(error => {
            setBusyMessage(null);
            axiosError(error, Navigate);
        });
    }

    const handleSelectionChange = (newCounts, changed) => {
        setCounts(newCounts);
        if (changed) setSelectionChanged(true);
    }

    const dryRun = () => {
        setBusyMessage(t('contracts.import.busy.dryRun'));

        axios.post(apiUrl + '/' + current.id + '/dry_run', {}, {headers}).then(response => {
            setBusyMessage(null);
            if (!isNull(response.data.report)) {
                setCurrent({...current, dry_run_report: response.data.report});
                setSelectionChanged(false);
            } else {
                errorStatus(response.data, t);
            }
        }).catch(error => {
            setBusyMessage(null);
            axiosError(error, Navigate);
        });
    }

    const confirmExecute = () => {
        const noDryRun = isNull(current.dry_run_report) || selectionChanged;

        Swal.fire({
            title: t('contracts.import.execute.title'),
            html: t('contracts.import.execute.text', {count: selectedCount}) + (noDryRun ? '<br/><br/><b>' + t('contracts.import.execute.noDryRun') + '</b>' : ''),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--theme-default)',
            confirmButtonText: t('contracts.import.execute.confirm'),
            cancelButtonText: t('cancel'),
        }).then(result => {
            if (result.isConfirmed) execute();
        })
    }

    const execute = () => {
        setBusyMessage(t('contracts.import.busy.execute'));

        axios.post(apiUrl + '/' + current.id + '/execute', {}, {headers}).then(response => {
            setBusyMessage(null);
            if (response.data.state !== 'success') {
                errorStatus(!isNull(response.data.report) ? response.data.report : response.data, t);
            }
            getImports(false);
            openImport(current.id);
        }).catch(error => {
            setBusyMessage(null);
            axiosError(error, Navigate);
        });
    }

    if (!isAdmin) {
        return <Redirect to="/contracts" replace />
    }

    return (
        <Fragment>
            <Breadcrumbs parent={t('breadcrumb.contracts')} title={t('contracts.import.title')} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card className="ribbon-wrapper" style={{ position: 'relative' }}>
                            {!isNull(busyMessage) && isNull(current) && <Loader />}
                            <CardBody>
                                <div className="ribbon ribbon-clip ribbon-primary">{t('contracts.import.title')}</div>
                                <Row className="align-items-end">
                                    <Col md="6">
                                        <label className="form-label" htmlFor="contract-import-file">{t('contracts.import.file')}</label>
                                        <Input
                                            key={isNull(file) ? 'empty' : 'chosen'}
                                            id="contract-import-file"
                                            className="visually-hidden"
                                            type="file"
                                            accept=".xlsx"
                                            onChange={event => setFile(event.target.files[0] || null)}
                                        />
                                        <div className="input-group">
                                            <label className="btn btn-secondary mb-0" htmlFor="contract-import-file">{t('contracts.import.chooseFile')}</label>
                                            <span className="form-control text-truncate">{isNull(file) ? t('contracts.import.noFileChosen') : file.name}</span>
                                        </div>
                                    </Col>
                                    <Col md="6">
                                        <Button color="primary" className="mt-2 me-2" disabled={isNull(file) || !isNull(busyMessage)} onClick={() => analyze(true)}>{t('contracts.import.compareFile')}</Button>
                                        {canReuseFile &&
                                            <Button color="secondary" className="mt-2 me-2" disabled={!isNull(busyMessage)} onClick={() => analyze(false)}>{t('contracts.import.compareLast')}</Button>
                                        }
                                    </Col>
                                </Row>
                                {!isNull(busyMessage) && isNull(current) && <div className="mt-3"><b>{busyMessage}</b></div>}

                                {!isNull(columnErrors) &&
                                    <div className="mt-4" id="contract-import-column-errors">
                                        <h6 className="text-danger">{t('contracts.import.columnErrors')}</h6>
                                        <Table bordered size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t('contracts.import.column')}</th>
                                                    <th>{t('contracts.import.expected')}</th>
                                                    <th>{t('contracts.import.found')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {columnErrors.map((error, index) => (
                                                    <tr key={'column-error-' + index}>
                                                        <td>{error.column}</td>
                                                        <td>{error.expected}</td>
                                                        <td>{error.found}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                }
                            </CardBody>
                        </Card>

                        {!isNull(current) &&
                            <Card style={{ position: 'relative' }}>
                                {!isNull(busyMessage) && <Loader />}
                                <CardBody>
                                    <h5>
                                        {current.file_name}{' '}
                                        <Badge color={isOpen ? 'primary' : 'success'}>{t('contracts.import.importStatus.' + current.status)}</Badge>
                                    </h5>
                                    {!isNull(current.file_modified_at) &&
                                        <p className="mb-2">{t('contracts.import.fileDate')}: <b>{displayDate(moment.utc(current.file_modified_at), 'date')}</b></p>
                                    }
                                    {!isNull(current.summary) && current.summary.emails_protected &&
                                        <p className="text-danger mb-2">{t('contracts.import.emailsProtected')}</p>
                                    }
                                    <div className="text-end mb-2">
                                        <Button color="secondary" size="sm" id="contract-import-open-compare" onClick={() => window.open(process.env.PUBLIC_URL + '/contracts/import/' + current.id + '/rows', '_blank')}>
                                            <i className="fa fa-external-link me-1"></i>{t('contracts.import.compare.open')}
                                        </Button>
                                    </div>
                                    <ImportRecords
                                        importData={current}
                                        apiUrl={apiUrl}
                                        rowsVersion={rowsVersion}
                                        counts={counts}
                                        onSelectionChange={handleSelectionChange}
                                    />
                                </CardBody>
                                {isOpen &&
                                    <CardFooter>
                                        <div className="d-flex flex-wrap justify-content-between align-items-center">
                                            <div id="contract-import-selected"><b>{t('contracts.import.selectedChanges')}: {selectedCount} / {isNull(counts) ? 0 : counts.total_changes}</b></div>
                                            {!isNull(busyMessage) && <div id="contract-import-busy"><b>{busyMessage}</b></div>}
                                            <div className="text-end">
                                                <Button color="secondary" className="mt-2 me-2" disabled={!isNull(busyMessage)} onClick={dryRun}>{t('contracts.import.dryRun')}</Button>
                                                <Button color="primary" className="mt-2 me-2" disabled={!isNull(busyMessage) || selectedCount === 0} onClick={confirmExecute}>{t('contracts.import.executeButton')}</Button>
                                            </div>
                                        </div>
                                    </CardFooter>
                                }
                            </Card>
                        }

                        {!isNull(current) && !isNull(current.execute_report) && <ImportReport report={current.execute_report} />}
                        {!isNull(current) && isOpen && !isNull(current.dry_run_report) && <ImportReport report={current.dry_run_report} outdated={selectionChanged} />}

                        {imports.length !== 0 &&
                            <Card>
                                <CardBody>
                                    <h5>{t('contracts.import.history')}</h5>
                                    <Table bordered size="sm" className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>{t('contracts.import.createdAt')}</th>
                                                <th>{t('contracts.import.file')}</th>
                                                <th>{t('contracts.import.status')}</th>
                                                <th>{t('contracts.import.executedAt')}</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {imports.map(item => (
                                                <tr key={'import-' + item.id}>
                                                    <td>{displayDate(moment.utc(item.created_at), 'datetime')}</td>
                                                    <td>{item.file_name}</td>
                                                    <td>{t('contracts.import.importStatus.' + item.status)}</td>
                                                    <td>{isNull(item.executed_at) ? '' : displayDate(moment.utc(item.executed_at), 'datetime')}</td>
                                                    <td className="text-end">
                                                        {item.status !== 'columns_mismatch' &&
                                                            <Button color="secondary" size="sm" onClick={() => openImport(item.id)}>{t('contracts.import.open')}</Button>
                                                        }
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </CardBody>
                            </Card>
                        }
                    </Col>
                </Row>
                <hr/>
                <div className='d-flex flex-wrap justify-content-between mb-4'>
                    <Button color="danger" className="mt-2 me-2" onClick={() => Navigate(-1)}>{t('back')}</Button>
                </div>
            </Container>
        </Fragment>
    )
}

export default ContractImport
